import { useQuery } from '@tanstack/react-query';

import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';

import { ArrowBack, OpenInNew } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { FileClient, FileViewModel } from '@@api';
import { formatTimestampFull, useApiClient } from '@@common';
import { AttributesTable, CopyableLink, CopyableText, DetailRow, DetailTable, ViewTitleBar } from '@@components';

function formatFileSize(size: number | undefined): string {
	if (size === undefined || size === null) {
		return '';
	}

	if (size < 1024) {
		return `${size} B`;
	}

	const units = ['KB', 'MB', 'GB', 'TB'];
	let value = size / 1024;
	let unitIndex = 0;

	while (value >= 1024 && unitIndex < units.length - 1) {
		value /= 1024;
		unitIndex += 1;
	}

	return `${value.toFixed(value >= 10 ? 1 : 2)} ${units[unitIndex]}`;
}

function isImageFile(file: FileViewModel): boolean {
	return !!file.url && !!file.contentType?.toLowerCase().startsWith('image/');
}

function isVideoFile(file: FileViewModel): boolean {
	return !!file.url && !!file.contentType?.toLowerCase().startsWith('video/');
}

function isPdfFile(file: FileViewModel): boolean {
	return !!file.url && file.contentType?.toLowerCase() === 'application/pdf';
}

function isJsonFile(file: FileViewModel): boolean {
	return !!file.url && file.contentType?.toLowerCase() === 'application/json';
}

function MediaPreview({ file }: { file: FileViewModel }) {
	const { data: jsonPreview, isLoading: isJsonLoading, error: jsonError } = useQuery({
		queryKey: ['files', 'json-preview', file.id, file.url],
		queryFn: async () => {
			const response = await fetch(file.url!);
			if (!response.ok) {
				throw new Error('Failed to load JSON preview.');
			}

			return response.text();
		},
		enabled: isJsonFile(file),
	});

	if (!file.url) {
		return (
			<Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
				<Typography color="text.secondary">No preview URL available</Typography>
			</Paper>
		);
	}

	if (isJsonFile(file)) {
		return (
			<Box
				component="pre"
				sx={{
					m: 0,
					p: 2,
					width: '100%',
					maxHeight: 720,
					overflow: 'auto',
					fontFamily: 'monospace',
					fontSize: 13,
					whiteSpace: 'pre-wrap',
					bgcolor: 'background.default',
				}}
			>
				{isJsonLoading ? 'Loading JSON...' : jsonError ? (jsonError instanceof Error ? jsonError.message : 'Failed to load JSON preview.') : jsonPreview}
			</Box>
		);
	}

	if (isImageFile(file)) {
		return (
			<Box
				component="img"
				src={file.url}
				alt={file.name}
				sx={{
					display: 'block',
					width: '100%',
					maxHeight: 560,
					objectFit: 'contain',
					bgcolor: 'background.default',
				}}
			/>
		);
	}

	if (isVideoFile(file)) {
		return (
			<Box
				component="video"
				src={file.url}
				controls
				preload="metadata"
				sx={{
					display: 'block',
					width: '100%',
					maxHeight: 560,
					bgcolor: 'common.black',
				}}
			/>
		);
	}

	if (isPdfFile(file)) {
		return (
			<Box
				component="iframe"
				src={file.url}
				title={file.name}
				sx={{
					display: 'block',
					width: '100%',
					height: { xs: 520, lg: 760 },
					border: 0,
					bgcolor: 'background.default',
				}}
			/>
		);
	}

	return (
		<Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
			<Typography color="text.secondary">No inline preview for this content type</Typography>
		</Paper>
	);
}

export default function FileDetailView() {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	const client = useApiClient(FileClient);

	const { data: file, isLoading, error } = useQuery({
		queryKey: ['files', 'detail', id],
		queryFn: () => client.getById(id!, undefined),
		enabled: !!id,
	});

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0 }}>
			<ViewTitleBar title={file?.name ?? 'File'}>
				<Stack direction="row" spacing={1}>
					<Button component={RouterLink} to={{ pathname: '/file', search: location.search }} startIcon={<ArrowBack />}>
						Back
					</Button>
					{file?.url && (
						<Button href={file.url} target="_blank" rel="noreferrer" startIcon={<OpenInNew />}>
							Open
						</Button>
					)}
				</Stack>
			</ViewTitleBar>

			{isLoading && (
				<Stack alignItems="center" sx={{ py: 6 }}>
					<CircularProgress />
				</Stack>
			)}

			{error && <Alert severity="error">{error instanceof Error ? error.message : 'Failed to load file.'}</Alert>}

			{file && (
				<Stack spacing={2} sx={{ px: 2, pb: 2, minWidth: 0 }}>
					<Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
						<DetailTable>
							<DetailRow label="ID" value={<CopyableText value={file.id} title="Copy ID to clipboard" />} />
							<DetailRow label="Name" value={<CopyableText value={file.name} title="Copy name to clipboard" />} />
							<DetailRow label="Size" value={formatFileSize(file.size)} />
							<DetailRow label="Content Type" value={file.contentType} />
							<DetailRow label="Created" value={file.created ? formatTimestampFull(file.created) : ''} />
							<DetailRow label="Updated" value={file.updated ? formatTimestampFull(file.updated) : ''} />
							<DetailRow label="Expires On" value={file.expiresAt ? formatTimestampFull(file.expiresAt) : 'No expiration'} />
							<DetailRow label="URL" value={<CopyableLink value={file.url} title="Copy URL to clipboard" />} />
						</DetailTable>
						<AttributesTable attributes={file.attributes} />
					</Stack>

					<Paper variant="outlined" sx={{ overflow: 'hidden' }}>
						<MediaPreview file={file} />
					</Paper>
				</Stack>
			)}
		</Stack>
	);
}
